import { combineReducers } from 'redux'
import { reducer as formReducer } from 'redux-form'
import crud_reducers from './crud_reducers'
import head from './head'
import logout from './logout'
import { logout as logoutAction } from '../actions/session'
import navigation from './navigation'
import draft from './draft'
import mentions from './mentions'
import attachments from './attachments'
import following_tags from './following_tags'
import tags from './tags'
import invitables from './invitables'
import groups from './groups'
import group from './group'
import posts from './posts'
import post from './post'
import responses from './responses'
import embeds from './embeds'
import sub_groups from './sub_groups'

const reducers = opts => ({
  head,
  navigation: navigation(opts),
  user: crud_reducers.one('user'),
  users: crud_reducers.list('users'),
  following: combineReducers({
    user: crud_reducers.one('following/user', {
      preserve_on_reload: false,
      preserve_on_update: false
    }),
    tag: crud_reducers.one('following/tag', {
      preserve_on_reload: false,
      preserve_on_update: false
    }),
    tags: crud_reducers.list('following/tags', false, following_tags),
    group: crud_reducers.one('following/group', {
      preserve_on_reload: false,
      preserve_on_update: false
    })
  }),
  instance: crud_reducers.one('instanceInfo'),
  session: crud_reducers.one('profile', true, logout),
  registration: combineReducers({
    email: combineReducers({
      register: crud_reducers.one('registerWithEmail'),
      confirm: combineReducers({
        pin: crud_reducers.one('confirmEmailWithPin'),
        token: crud_reducers.one('confirmEmailWithToken')
      })
    }),
    tags: crud_reducers.one('onboardingTags')
  }),
  draft: crud_reducers.one(
    'draft',
    { preserve_on_reload: true, preserve_on_update: true },
    draft
  ),
  drafts: crud_reducers.list('drafts'),
  post: crud_reducers.one('post', {
    preserve_on_reload: true,
    preserve_on_update: true
  }, post),
  posts: crud_reducers.list('posts', true, posts),
  embeds: embeds,
  references: crud_reducers.list('references'),
  response: crud_reducers.one('response', true),
  responses: crud_reducers.list('responses', true, responses),
  reactions: crud_reducers.list('reactions', true),
  mentions: crud_reducers.list('mentions', true, mentions),
  attachments,
  activities: combineReducers({
    flat: crud_reducers.list('activities/flat', true),
    aggregated: crud_reducers.list('activities/aggregated', true),
    tags: crud_reducers.list('activities/tags', false),
    tag: crud_reducers.list('activities/tag', false),
    group: crud_reducers.list('activities/group', true)
  }),
  notifications: crud_reducers.list('notifications'),
  groups: crud_reducers.list('groups', true, groups),
  group: crud_reducers.one(
    'group',
    {
      preserve_on_reload: true,
      preserve_on_update: true
    },
    group
  ),
  'sub-groups': crud_reducers.list('sub-groups', true, sub_groups),
  discover: crud_reducers.one('discover', true),
  membership: crud_reducers.one('membership', {
    preserve_on_reload: true,
    preserve_on_update: true
  }),
  members: crud_reducers.list('members', true),
  invites: crud_reducers.list('invites', true),
  invitables: crud_reducers.list('invitables', true, invitables),
  invitation: crud_reducers.one('invitation', true),
  invitations: crud_reducers.one('invitations', true),
  access_request: crud_reducers.one('access_request', true),
  access_requests: crud_reducers.list('access_requests', true),
  tags: crud_reducers.list('tags', true, tags),
  form: formReducer
})

export default (others, opts) => {
  const rootReducer = combineReducers({
    ...reducers(opts),
    ...others
  })
  // `navigation` isn't user data, but its reducer mutates its state object in
  // place and its default is a closure captured at store-creation time (the
  // very first route requested) rather than a fresh `{}` — resetting it would
  // snap the nav stack back to that first route instead of the current one.
  const preservedKeys = Object.keys(others || {}).concat('navigation')

  // Logout only ever reset the `session` slice (wired above via the `logout`
  // extra-reducer) — every other slice (groups, group, following, members,
  // notifications, drafts, ...) kept whatever the previous user had loaded,
  // so navigating to a page client-side right after logout could still show
  // their (possibly private) data. Wipe everything except the slices above
  // so the next render starts clean, same as a fresh unauthenticated load.
  //
  // Reset on REQUEST as well as SUCCESS, matching the session reducer: it
  // clears optimistically on REQUEST, which is what flips the UI to logged-out
  // and redirects (LogoutPage watches isLoggedIn). If the logout call then
  // fails we'd otherwise be left looking logged out while still holding the
  // previous user's data — the exact leak this guards against.
  const isLogout = type =>
    type === logoutAction.REQUEST || type === logoutAction.SUCCESS

  return (state, action) => {
    if (state && isLogout(action.type)) {
      state = preservedKeys.reduce((kept, key) => {
        kept[key] = state[key]
        return kept
      }, {})
    }
    return rootReducer(state, action)
  }
}
